import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Monitor, RotateCcw, Move } from "lucide-react";

interface BannerImagePositionEditorProps {
  imageUrl?: string;
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'custom';
  objectPosition?: string;
  scale?: number;
  onPositionChange: (position: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'custom') => void;
  onObjectPositionChange: (objectPosition: string) => void;
  onScaleChange: (scale: number) => void;
}

export const BannerImagePositionEditor: React.FC<BannerImagePositionEditorProps> = ({
  imageUrl,
  position = 'center',
  objectPosition = 'center center',
  scale = 1,
  onPositionChange,
  onObjectPositionChange,
  onScaleChange
}) => {
  const [previewPosition, setPreviewPosition] = useState(objectPosition);
  const [previewScale, setPreviewScale] = useState(scale);
  const [isDragging, setIsDragging] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Presets de posicionamento
  const positionPresets = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center'
  };

  // Função para detectar e corrigir inconsistências
  const detectAndFixInconsistency = (pos: string, objPos: string) => {
    console.log('🔍 [BannerImagePositionEditor] Verificando consistência:', {
      position: pos, objectPosition: objPos
    });

    // Se a posição é um preset mas o objectPosition não corresponde
    if (pos in positionPresets && positionPresets[pos as keyof typeof positionPresets] !== objPos) {
      const correctObjectPosition = positionPresets[pos as keyof typeof positionPresets];
      console.log('⚠️ [BannerImagePositionEditor] Inconsistência detectada! Corrigindo:', {
        from: objPos,
        to: correctObjectPosition
      });
      
      // Corrigir automaticamente
      setPreviewPosition(correctObjectPosition);
      onObjectPositionChange(correctObjectPosition);
      return correctObjectPosition;
    }
    
    return objPos;
  };

  // Função para detectar qual preset deveria estar ativo baseado no objectPosition
  const detectActivePreset = (objPos: string): string => {
    for (const [presetKey, presetValue] of Object.entries(positionPresets)) {
      if (presetValue === objPos) {
        return presetKey;
      }
    }
    return 'custom';
  };

  // Sincronizar estado local com props recebidas
  useEffect(() => {
    console.log('🔄 [BannerImagePositionEditor] Props recebidas:', {
      position, objectPosition, scale
    });
    console.log('🔄 [BannerImagePositionEditor] Estado atual antes da sincronização:', {
      previewPosition, previewScale
    });

    // Detectar e corrigir inconsistências
    const correctedObjectPosition = detectAndFixInconsistency(position, objectPosition);
    
    // Detectar se a position precisa ser corrigida baseada no objectPosition
    const detectedPreset = detectActivePreset(correctedObjectPosition);
    if (detectedPreset !== position && detectedPreset !== 'custom') {
      console.log('🔧 [BannerImagePositionEditor] Corrigindo position inconsistente:', {
        from: position,
        to: detectedPreset,
        basedOnObjectPosition: correctedObjectPosition
      });
      onPositionChange(detectedPreset as any);
    }
    
    setPreviewPosition(correctedObjectPosition);
    setPreviewScale(scale);
    
    console.log('🔄 [BannerImagePositionEditor] Estado atualizado para:', {
      previewPosition: correctedObjectPosition, previewScale: scale
    });
  }, [objectPosition, scale, position]);

  // Converter posição de clique para object-position
  const handlePreviewClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!previewRef.current) return;

    const rect = previewRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    const newPosition = `${x.toFixed(1)}% ${y.toFixed(1)}%`;
    setPreviewPosition(newPosition);
    onObjectPositionChange(newPosition);
    onPositionChange('custom');
  };

  // Aplicar preset de posicionamento
  const applyPositionPreset = (preset: keyof typeof positionPresets) => {
    const newPosition = positionPresets[preset];
    console.log('🎯 [BannerImagePositionEditor] Aplicando preset:', {
      preset, newPosition
    });
    setPreviewPosition(newPosition);
    onObjectPositionChange(newPosition);
    onPositionChange(preset);
  };

  // Reset para valores padrão
  const resetToDefaults = () => {
    setPreviewPosition('center center');
    setPreviewScale(1);
    onObjectPositionChange('center center');
    onScaleChange(1);
    onPositionChange('center');
  };

  // Atualizar escala
  const handleScaleChange = (newScale: number[]) => {
    const scaleValue = newScale[0];
    setPreviewScale(scaleValue);
    onScaleChange(scaleValue);
  };

  // Estilo para a imagem de preview
  const getImageStyle = () => ({
    objectPosition: previewPosition,
    transform: `scale(${previewScale})`,
    transition: isDragging ? 'none' : 'all 0.2s ease'
  });

  // Detectar posição ativa atual baseada no previewPosition
  const currentActivePosition = detectActivePreset(previewPosition);

  return (
    <Card className="bg-[#2A3441] border-[#3A4451]">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Monitor className="w-5 h-5" />
          Posicionamento da Imagem Desktop
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Preview da Imagem */}
        {imageUrl && (
          <div className="space-y-4">
            <Label className="text-white">Preview (Clique para ajustar posição)</Label>
            <div 
              ref={previewRef}
              className="relative w-full h-48 bg-gray-800 rounded-lg overflow-hidden cursor-crosshair border-2 border-dashed border-gray-600 hover:border-[#00B6F0] transition-colors"
              onClick={handlePreviewClick}
              title="Clique para definir o ponto focal da imagem"
            >
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Preview"
                className="w-full h-full object-cover"
                style={getImageStyle()}
              />
              
              {/* Indicador de ponto focal */}
              {currentActivePosition === 'custom' && (
                <div 
                  className="absolute w-3 h-3 bg-[#00B6F0] border-2 border-white rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  style={{
                    left: previewPosition.split(' ')[0],
                    top: previewPosition.split(' ')[1]
                  }}
                />
              )}
              
              {/* Grid de ajuda */}
              <div className="absolute inset-0 pointer-events-none opacity-20">
                <div className="absolute top-1/3 left-0 right-0 h-px bg-white"></div>
                <div className="absolute top-2/3 left-0 right-0 h-px bg-white"></div>
                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white"></div>
                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white"></div>
              </div>
            </div>
          </div>
        )}

        {/* Presets de Posicionamento */}
        <div className="space-y-3">
          <Label className="text-white">Posições Rápidas</Label>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(positionPresets).map(([key, value]) => (
              <Button
                key={key}
                variant={currentActivePosition === key ? "default" : "outline"}
                size="sm"
                onClick={() => applyPositionPreset(key as keyof typeof positionPresets)}
                className={`text-xs ${
                  currentActivePosition === key 
                    ? "bg-[#00B6F0] text-black" 
                    : "border-[#3A4451] text-white hover:bg-[#3A4451]"
                }`}
              >
                {key === 'center' ? 'Centro' : 
                 key === 'top' ? 'Topo' :
                 key === 'bottom' ? 'Base' :
                 key === 'left' ? 'Esquerda' : 'Direita'}
              </Button>
            ))}
          </div>
        </div>

        {/* Controle de Escala */}
        <div className="space-y-3">
          <Label className="text-white">
            Zoom da Imagem: {Math.round(previewScale * 100)}%
          </Label>
          <Slider
            value={[previewScale]}
            onValueChange={handleScaleChange}
            min={0.5}
            max={2}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>50%</span>
            <span>100%</span>
            <span>200%</span>
          </div>
        </div>

        {/* Posição Customizada */}
        {currentActivePosition === 'custom' && (
          <div className="space-y-3">
            <Label className="text-white">Posição Personalizada</Label>
            <Input
              value={previewPosition}
              onChange={(e) => {
                setPreviewPosition(e.target.value);
                onObjectPositionChange(e.target.value);
              }}
              placeholder="Ex: 25% 75%, left top, center bottom"
              className="bg-[#181D24] border-[#3A4451] text-white"
            />
            <p className="text-xs text-gray-400">
              Use percentuais (25% 75%) ou palavras-chave (left, center, right, top, bottom)
            </p>
          </div>
        )}

        {/* Botões de Ação */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="border-[#3A4451] text-white hover:bg-[#3A4451]"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Resetar
          </Button>
        </div>

        {/* Dicas */}
        <div className="bg-[#181D24] p-3 rounded-lg">
          <h4 className="text-sm font-medium text-white mb-2">💡 Dicas:</h4>
          <ul className="text-xs text-gray-400 space-y-1">
            <li>• Clique na imagem para definir o ponto focal</li>
            <li>• Use zoom para destacar partes específicas</li>
            <li>• O grid de ajuda mostra a regra dos terços</li>
            <li>• Teste em diferentes tamanhos de tela</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}; 